import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import ozenLogo from '../assets/ozen.png';
import QRCode from 'qrcode';
import CryptoJS from 'crypto-js';

// Секретный ключ для шифрования (в реальном приложении должен храниться в безопасном месте)
const SECRET_KEY = 'ozen_guarantor_secret_key_2024';

// Функция для дешифровки хэша
export const decryptHash = (encryptedHash) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedHash, SECRET_KEY);
    const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    return decryptedData;
  } catch (error) {
    return null;
  }
};

export const generateGuarantorPDF = async (formData) => {
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  
  // Загружаем шрифт и логотип
  const fontUrl = 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Me5Q.ttf';
  const fontBytes = await fetch(fontUrl).then(res => res.arrayBuffer());
  const customFont = await doc.embedFont(fontBytes);

  // Загружаем логотип
  const logoResponse = await fetch(ozenLogo);
  const logoImageBytes = await logoResponse.arrayBuffer();
  const logoImage = await doc.embedPng(logoImageBytes);

  // Генерируем зашифрованный хэш на основе SMS-кода и данных
  const dataToEncrypt = {
    smsCode: formData.smsCode,
    iin: formData.iin,
    timestamp: new Date().toISOString()
  };
  
  const encryptedHash = CryptoJS.AES.encrypt(
    JSON.stringify(dataToEncrypt),
    SECRET_KEY
  ).toString();

  // Генерируем QR код с данными и хэшем
  const qrData = JSON.stringify({
    fullName: formData.fullName,
    iin: formData.iin,
    timestamp: new Date().toISOString(),
    hash: encryptedHash
  });
  
  const qrCodeUrl = await QRCode.toDataURL(qrData);
  const qrCodeImage = await doc.embedPng(qrCodeUrl.split(',')[1]);
  
  const page = doc.addPage([595, 842]); // A4
  const { width, height } = page.getSize();

  // Добавляем водяные знаки
  const watermarkSize = 100;
  const watermarkGap = 200;
  const watermarkOpacity = 0.03; // Очень светлые водяные знаки

  // Создаем паттерн из логотипов
  for (let y = 0; y < height + watermarkGap; y += watermarkGap) {
    for (let x = 0; x < width + watermarkGap; x += watermarkGap) {
      // Рисуем логотип как водяной знак
      page.drawImage(logoImage, {
        x: x - watermarkSize/2,
        y: y - watermarkSize/2,
        width: watermarkSize,
        height: watermarkSize,
        opacity: watermarkOpacity,
        rotate: { type: 'degrees', angle: 45 }
      });

      // Добавляем текст OZEN рядом с логотипом
      page.drawText('OZEN', {
        x: x + watermarkSize/2,
        y: y,
        size: 16,
        font: customFont,
        color: rgb(0.95, 0.95, 0.95),
        rotate: { type: 'degrees', angle: 45 },
        opacity: watermarkOpacity
      });
    }
  }

  // Добавляем диагональную надпись "КОПИЯ"
  const copyText = 'КОПИЯ • КӨШІРМЕ • ';
  const repeatedCopyText = copyText.repeat(3);
  page.drawText(repeatedCopyText, {
    x: 0,
    y: height/2,
    size: 24,
    font: customFont,
    color: rgb(0.9, 0.9, 0.9),
    rotate: { type: 'degrees', angle: -45 },
    opacity: 0.1
  });

  // Остальной контент поверх водяных знаков
  // Добавляем логотип
  const logoSize = 40;
  page.drawImage(logoImage, {
    x: 50,
    y: height - 90,
    width: logoSize,
    height: logoSize
  });

  // Заголовок на двух языках
  page.drawText('Анкета гаранта / Кепілгердің сауалнамасы', {
    x: 50,
    y: height - 120,
    size: 24,
    font: customFont,
    color: rgb(0.2, 0.2, 0.2)
  });

  // Создаем единый блок внизу
  const bottomY = 70;
  const blockHeight = 60;

  // Общий блок
  page.drawRectangle({
    x: 50,
    y: bottomY,
    width: width - 100,
    height: blockHeight,
    color: rgb(0.98, 0.98, 0.98),
    borderColor: rgb(0.9, 0.9, 0.9),
    borderWidth: 1,
  });

  // Вертикальная разделительная линия
  page.drawLine({
    start: { x: width/2, y: bottomY },
    end: { x: width/2, y: bottomY + blockHeight },
    thickness: 1,
    color: rgb(0.9, 0.9, 0.9),
  });

  // Левая часть - информация о подписи
  page.drawText('ПОДПИСАНО С OTP / OTP-МЕН ҚОЛТАҢБА', {
    x: 60,
    y: bottomY + 45,
    size: 8,
    font: customFont,
    color: rgb(0.5, 0.5, 0.5)
  });

  const timestamp = new Date().toLocaleString('ru-RU');
  page.drawText(`Подписано: ${timestamp}`, {
    x: 60,
    y: bottomY + 30,
    size: 10,
    font: customFont,
    color: rgb(0.2, 0.2, 0.2)
  });

  // Используем зашифрованный хэш
  page.drawText(`Хэш: ${encryptedHash.substring(0, 20)}...`, {
    x: 60,
    y: bottomY + 15,
    size: 10,
    font: customFont,
    color: rgb(0.2, 0.2, 0.2)
  });

  // Правая часть - QR код и пояснение
  const qrSize = 45;

  // Делим правую часть на две колонки
  const rightHalfWidth = (width - 100)/2;
  const textColumnWidth = rightHalfWidth * 0.5; // 50% под текст
  const qrColumnWidth = rightHalfWidth * 0.5; // 50% под QR

  // Пояснение для QR кода (в левой колонке правой части)
  page.drawText('Проверка подлинности /', {
    x: width/2 + 20, // Отступ от разделительной линии
    y: bottomY + 45,
    size: 8,
    font: customFont,
    color: rgb(0.5, 0.5, 0.5)
  });

  page.drawText('Түпнұсқалықты тексеру', {
    x: width/2 + 20,
    y: bottomY + 35,
    size: 8,
    font: customFont,
    color: rgb(0.5, 0.5, 0.5)
  });

  // QR код в правой колонке правой части
  const qrX = width/2 + textColumnWidth + (qrColumnWidth - qrSize)/2;

  // Добавляем белый фон под QR код
  page.drawRectangle({
    x: qrX - 2,
    y: bottomY + 6,
    width: qrSize + 4,
    height: qrSize + 4,
    color: rgb(1, 1, 1),
    opacity: 0.05
  });

  // Рамка для QR кода
  page.drawRectangle({
    x: qrX - 2,
    y: bottomY + 6,
    width: qrSize + 4,
    height: qrSize + 4,
    borderColor: rgb(0.8, 0.8, 0.8),
    borderWidth: 1,
    color: rgb(1, 1, 1),
    opacity: 0
  });

  // Сам QR код
  page.drawImage(qrCodeImage, {
    x: qrX,
    y: bottomY + 8,
    width: qrSize,
    height: qrSize
  });

  // Добавляем уголки вокруг QR кода
  const cornerSize = 4;
  const corners = [
    // Верхний левый
    { start: { x: qrX - 2, y: bottomY + qrSize + 10 }, end: { x: qrX - 2 + cornerSize, y: bottomY + qrSize + 10 } },
    { start: { x: qrX - 2, y: bottomY + qrSize + 10 }, end: { x: qrX - 2, y: bottomY + qrSize + 10 - cornerSize } },
    // Верхний правый
    { start: { x: qrX + qrSize + 2, y: bottomY + qrSize + 10 }, end: { x: qrX + qrSize + 2 - cornerSize, y: bottomY + qrSize + 10 } },
    { start: { x: qrX + qrSize + 2, y: bottomY + qrSize + 10 }, end: { x: qrX + qrSize + 2, y: bottomY + qrSize + 10 - cornerSize } },
    // Нижний левый
    { start: { x: qrX - 2, y: bottomY + 6 }, end: { x: qrX - 2 + cornerSize, y: bottomY + 6 } },
    { start: { x: qrX - 2, y: bottomY + 6 }, end: { x: qrX - 2, y: bottomY + 6 + cornerSize } },
    // Нижний правый
    { start: { x: qrX + qrSize + 2, y: bottomY + 6 }, end: { x: qrX + qrSize + 2 - cornerSize, y: bottomY + 6 } },
    { start: { x: qrX + qrSize + 2, y: bottomY + 6 }, end: { x: qrX + qrSize + 2, y: bottomY + 6 + cornerSize } }
  ];

  corners.forEach(corner => {
    page.drawLine({
      start: corner.start,
      end: corner.end,
      thickness: 1,
      color: rgb(0.7, 0.7, 0.7)
    });
  });

  // Функция для добавления строки таблицы
  const addTableRow = (label, value, yPosition) => {
    // Фон строки
    page.drawRectangle({
      x: 50,
      y: yPosition - 25,
      width: width - 100,
      height: 40,
      color: rgb(0.98, 0.98, 0.98),
      borderColor: rgb(0.9, 0.9, 0.9),
      borderWidth: 1,
    });

    // Метка
    page.drawText(label, {
      x: 60, // Отступ слева
      y: yPosition,
      size: 8, // Уменьшили размер
      font: customFont,
      color: rgb(0.5, 0.5, 0.5)
    });

    // Значение
    page.drawText(value || '', {
      x: 60,
      y: yPosition - 15,
      size: 11, // Уменьшили размер
      font: customFont,
      color: rgb(0.2, 0.2, 0.2)
    });
  };

  // Добавляем поля с отступами
  const startY = height - 180;
  const gap = 50; // Уменьшили отступ между полями

  addTableRow('ФИО / АТЫ-ЖӨНІ', formData.fullName, startY);
  addTableRow('ИИН / ЖСН', formData.iin, startY - gap);
  addTableRow('ДАТА РОЖДЕНИЯ / ТУҒАН КҮНІ', formData.birthDate, startY - gap * 2);
  addTableRow('НОМЕР УДОСТОВЕРЕНИЯ / КУӘЛІК НӨМІРІ', formData.idNumber, startY - gap * 3);
  addTableRow('ДАТА ВЫДАЧИ / БЕРІЛГЕН КҮНІ', formData.idIssueDate, startY - gap * 4);
  addTableRow('ДЕЙСТВИТЕЛЬНО ДО / ЖАРАМДЫ', formData.idExpiryDate, startY - gap * 5);
  addTableRow('АДРЕС РЕГИСТРАЦИИ / ТІРКЕУ МЕКЕНЖАЙЫ', formData.registrationAddress, startY - gap * 6);
  addTableRow('АДРЕС ПРОЖИВАНИЯ / ТҰРҒЫЛЫҚТЫ МЕКЕНЖАЙЫ', formData.address, startY - gap * 7);
  addTableRow('ТЕЛЕФОН / ТЕЛЕФОН', formData.phone, startY - gap * 8);

  // Добавляем полупрозрачную плашку под контентом для лучшей читаемости
  page.drawRectangle({
    x: 40,
    y: 60,
    width: width - 80,
    height: height - 120,
    color: rgb(1, 1, 1),
    opacity: 0.02
  });

  // Генерируем и возвращаем PDF как Blob
  const pdfBytes = await doc.save();
  return new Blob([pdfBytes], { type: 'application/pdf' });
}; 